"use client";

import React from "react";
import { Activity, ArrowRight, Boxes, CircleCheck as CheckCircle2, Layers, Pill, Recycle, ScanLine, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface SolutionItem {
  id: string;
  title: string;
  summary: string;
  path: string;
  iconName: "pat" | "powder" | "tablet" | "safety" | "hplc" | "rmid";
  badge: string;
  highlight: string;
}
const solutions: SolutionItem[] = [{
  id: "pat",
  title: "Process Analytical Technology",
  summary: "Real-time in-line and at-line NIR spectroscopic monitoring for continuous process control.",
  path: "/ProcessAnalyticalTechnologyPage",
  iconName: "pat",
  badge: "Process Monitoring",
  highlight: "In-line PAT"
}, {
  id: "powder",
  title: "Powder Testing",
  summary: "Characterise flowability, shear strength, tap density, and compressibility under GMP.",
  path: "/PowderTestingPage",
  iconName: "powder",
  badge: "Physical Testing",
  highlight: "USP/Ph.Eur. Compliant"
}, {
  id: "tablet",
  title: "Tablet Testing",
  summary: "Automated disintegration, dissolution, hardness, and friability testing instrumentation.",
  path: "/TabletTestingPage",
  iconName: "tablet",
  badge: "Solid Dose QC",
  highlight: "Automated Testing"
}, {
  id: "safety",
  title: "Safety & Security",
  summary: "Handheld 1064 nm Raman analysers for rapid identification of narcotics and hazardous substances.",
  path: "/SafetyAndSecurityPage",
  iconName: "safety",
  badge: "Field & HazMat",
  highlight: "1064nm Raman"
}, {
  id: "hplc",
  title: "HPLC Solvent Recovery (ReSolve)",
  summary: "Automated isocratic solvent recycling systems reducing lab solvent waste by up to 90%.",
  path: "/HplcSolventRecoveryResolvePage",
  iconName: "hplc",
  badge: "Sustainability",
  highlight: "Up to 90% Recovery"
}, {
  id: "rmid",
  title: "Raw Material Identification",
  summary: "High-throughput non-destructive container verification compliant with 21 CFR Part 11.",
  path: "/RawMaterialIdentificationPage",
  iconName: "rmid",
  badge: "Warehouse QC",
  highlight: "Through-Container ID"
}];
function renderSolutionIcon(type: SolutionItem["iconName"]) {
  const iconClass = "size-5 text-primary";
  switch (type) {
    case "pat":
      return <Activity className={iconClass} aria-hidden="true" />;
    case "powder":
      return <Layers className={iconClass} aria-hidden="true" />;
    case "tablet":
      return <Pill className={iconClass} aria-hidden="true" />;
    case "safety":
      return <ShieldCheck className={iconClass} aria-hidden="true" />;
    case "hplc":
      return <Recycle className={iconClass} aria-hidden="true" />;
    case "rmid":
      return <ScanLine className={iconClass} aria-hidden="true" />;
    default:
      return <Boxes className={iconClass} aria-hidden="true" />;
  }
}
export default function RelatedSolutions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.215, 0.61, 0.355, 1]
      }
    }
  };
  return <section id="relatedsolutions" data-nav="light" className="bg-background py-16 md:py-24 border-t border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="font-semibold text-secondary-foreground px-3 py-1 text-xs uppercase tracking-wider">
              Analytical Disciplines
            </Badge>
          </div>
          
          <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground text-balance">
            Related Instrumentation & Solution Hubs
          </h2>
          
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Explore complementary analytical techniques, qualification services, and process control platforms designed for regulated laboratory and manufacturing teams across Ireland and the UK.
          </p>
        </div>

        {/* Solutions Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }}>
          {solutions.map((solution, i) => {
          const isAlternate = i % 2 === 1;
          return <motion.div key={solution.id} data-index={i} variants={itemVariants} className="h-full flex">
                <Card className={`group relative flex flex-col justify-between w-full border border-border transition-[box-shadow,border-color] duration-300 hover:border-primary/50 hover:shadow-md ${isAlternate ? "bg-secondary/35" : "bg-card"}`}>
                  <CardContent className="p-6 sm:p-7 flex flex-col flex-1">
                    {/* Top row: Icon and Tag */}
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <div className="size-11 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                        {renderSolutionIcon(solution.iconName)}
                      </div>
                      <Badge variant="outline" className="text-[11px] font-medium text-muted-foreground border-border bg-background/80">
                        {solution.badge}
                      </Badge>
                    </div>

                    {/* Title & Description */}
                    <div className="flex-1 mb-6">
                      <h3 className="font-default text-xl font-bold text-foreground mb-2.5 group-hover:text-primary transition-colors duration-200">
                        {solution.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                        {solution.summary}
                      </p>
                    </div>

                    {/* Key feature callout */}
                    <div className="pt-4 border-t border-border/70 mb-5 flex items-center gap-2">
                      <CheckCircle2 className="size-4 text-primary shrink-0" aria-hidden="true" />
                      <span className="text-xs font-semibold text-secondary-foreground">
                        {solution.highlight}
                      </span>
                    </div>

                    {/* Action link */}
                    <div>
                      <Button asChild variant="ghost" size="sm" className="w-full justify-between px-3 h-10 font-medium text-foreground bg-background hover:bg-primary hover:text-primary-foreground border border-border group-hover:border-primary transition-colors">
                        <Link to={solution.path} aria-label={`Explore ${solution.title}`}>
                          <span>Explore Solution</span>
                          <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Context Support Bar */}
        <div className="mt-12 p-6 sm:p-8 rounded-xl bg-secondary/50 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="max-w-xl">
            <h4 className="font-default text-base sm:text-lg font-semibold text-foreground">
              Require application consultation or method feasibility?
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              Our technical specialists evaluate sample compatibility and provide bespoke instrument configuration support.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <Button asChild variant="default" size="default" className="shadow-xs">
              <Link to="/ApplicationConsultationPage">
                Book Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="default">
              <Link to="/ContactPage">
                Contact Technical Team
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}